import React, { useState, useEffect, useRef } from 'react'
import clsx from 'clsx'

import { BonusModal, BonusModalHeader, BonusModalBody, BonusModalFooter } from '@components'
import { buildGradientStyle } from '@utils/buildGradientStyle'
import { buildGradientBorderStyle } from '@utils/buildGradientBorderStyle'

import { useConfigurationStore, useBetSliderStore, usePlayerInfoStore, useBuyBonusStore } from '@stores'

import buyButton from '@assets/buy-bonus/button-buy-enabled.svg'
import toggleCircle from '@assets/buy-bonus/toggle-circle.svg'
import {
    BuyBonusAtlasMapMessage,
    BuyBonusConfirmButtonClickedMessage,
    BuyBonusDecreaseButtonClickedMessage,
    BuyBonusFeature,
    BuyBonusGuaranteeButtonClickedMessage,
    BuyBonusIncreaseButtonClickedMessage,
    BuyBonusMessages,
    BuyBonusRejectedMessage,
    EngineBetSliderSetScrollingIndexMessage,
    GameUIMessageScope,
    GameUIMessageType,
    ViewportType,
} from 'game-ui-types'
import { ArrowButtonState, ArrowButtonType } from '@constants/buyBonus.ts'
import { buildAtlasImageStyles } from '@utils/buildAtlasImageStyles'
import { useScaleToFit } from '@hooks'
import { formatMoney } from '@utils/money'
import { BuyBonusOptionButtonClickedMessage } from 'game-ui-types'
import { getBetValue } from '@utils/betOption'
import { useTranslation } from '@amusnet/translations/react'

interface BuyBonusModalProps {
    isOpen: boolean
    onClose: () => void
}

const BuyBonusModal: React.FC<BuyBonusModalProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation()
    const balance = usePlayerInfoStore((state) => state.balance)

    const betOptions = useConfigurationStore((state) => state.betOptions)
    const activeIndex = useBetSliderStore((state) => state.activeIndex)
    const updateScrollToIndex = useBetSliderStore((state) => state.updateScrollToIndex)
    const currency = useConfigurationStore((state) => state.currency)
    const viewport = useConfigurationStore((state) => state.viewport)
    const currencyHideCents = useConfigurationStore((state) => state.currencyHideCents)
    const buyBonusConfig = useConfigurationStore((state) => state.buyBonusConfig)

    const findElementByImageName = useBuyBonusStore((state) => state.findElementByImageName)
    const buyBonusStoreConfig = useBuyBonusStore((state) => state.buyBonusConfig)
    const { maxBet, layout, choiceCount, features } = buyBonusStoreConfig
    const setSelectedFeature = useBuyBonusStore((state) => state.setSelectedFeature)
    const setMultiChoiceToggle = useBuyBonusStore((state) => state.setMultiChoiceToggle)
    const updateCurrentFeatureMultiplier = useBuyBonusStore((state) => state.updateCurrentFeatureMultiplier)
    const hasGuaranteedWin = useBuyBonusStore((state) => state.hasGuaranteedWin)
    const selectedFeatureIndex = useBuyBonusStore((state) => state.selectedFeatureIndex)
    const isGuaranteedWinEnabled = useBuyBonusStore((state) => state.isGuaranteedWinEnabled)
    const setIsGuaranteedWinEnabled = useBuyBonusStore((state) => state.setIsGuaranteedWinEnabled)
    const isForceGuaranteedWinEnabled = useBuyBonusStore((state) => state.isForceGuaranteedWinEnabled)
    const setIsForceGuaranteedWinEnabled = useBuyBonusStore((state) => state.setIsForceGuaranteedWinEnabled)
    const preloadImages = useBuyBonusStore((state) => state.preloadImages)
    const handleAtlasTexturesData = useBuyBonusStore((state) => state.handleAtlasTexturesData)
    const addAtlasMap = useBuyBonusStore((state) => state.addAtlasMap)
    const setSelectedFeatureIndex = useBuyBonusStore((state) => state.setSelectedFeatureIndex)
    const cachedImages = useBuyBonusStore((state) => state.cachedImages)

    const [activeBetIndex, setActiveBetIndex] = useState(activeIndex)
    const [rightArrowState, setRightArrowState] = useState(ArrowButtonState.ENABLED)
    const [leftArrowState, setLeftArrowState] = useState(ArrowButtonState.ENABLED)
    const [isRightArrowDisabled, setIsRightArrowDisabled] = useState(false)
    const [isLeftArrowDisabled, setIsLeftArrowDisabled] = useState(false)
    const [confirmButtonDisabled, setConfirmButtonDisabled] = useState(false)

    const activeBet = betOptions[activeBetIndex]

    const featureOptionRef = React.useRef<HTMLDivElement>(null)
    const featureOptionPortraitRef = React.useRef<HTMLDivElement>(null)
    const featureAmountRef = React.useRef<HTMLDivElement>(null)

    const scrollRef = React.useRef<HTMLDivElement | null>(null)

    const portraitViewport = viewport === ViewportType.MOBILE_PORTRAIT
    const isDownRef = useRef(false)
    const isDraggingRef = useRef(false)
    const startXRef = useRef(0)
    const startScrollLeftRef = useRef(0)
    const pointerIdRef = useRef<number | null>(null)
    const cancelNextClickRef = useRef(false)
    const isDesktop = viewport === ViewportType.DESKTOP

    const DRAG_THRESHOLD_X = 6

    const isMultiChoice = choiceCount > 1
    const shouldAddScroll = features.length > 3

    const allSymbolStyles = buildGradientStyle(layout.allSymbolsBackgroundColors)
    const isGuaranteed = isGuaranteedWinEnabled || isForceGuaranteedWinEnabled

    const guaranteedWinBgStyle = buildGradientStyle(layout.guaranteedWinBackgroundColors, 'to bottom')
    const guaranteedWinActiveBgStyle = buildGradientStyle(layout.activeGuaranteedWinBackgroundColors, 'to bottom')
    const guaranteedWinBaseClasses =
        'bg-gradient-to-b rounded-[10px] text-center p-1 transition-all ease-in-out duration-350 mt-[20px]'
    const guaranteedWinBorderStyles = buildGradientBorderStyle(['#FFA146', '#FFF089', '#FFA146'], 2, 50)
    const togglePositionClasses = clsx(
        'absolute top-[6px] left-[6px] transition-all ease-in-out duration-200',
        isGuaranteed ? 'left-[calc(100%-6px)] -translate-x-full' : 'translate-x-0'
    )
    const toggleBgStyle = buildGradientStyle(
        isGuaranteed ? ['#306611', '#52AF1C'] : layout.guaranteedWinToggleBackgroundColors
    )

    const symbolBorderColors = layout.symbolBorderColors ?? ['#FFA146', '#FFF089', '#FFA146']
    const selectedSymbolBorderColors = layout.selectedSymbolBorderColors ?? symbolBorderColors
    const symbolOptionWidth = portraitViewport ? '100%' : '240px'
    const symbolOptionHeight = portraitViewport ? '180px' : '290px'

    const betAmountBgStyle = buildGradientStyle(layout.amountInputBackgroundColors)

    const modalBodyPadding = !isMultiChoice ? '0 0 24px' : portraitViewport ? '20px 40px' : '14px 20px'

    const handleMessage = (event: MessageEvent) => {
        const { scope, type } = event.data

        if (scope !== GameUIMessageScope.BUY_BONUS) {
            return
        }

        if (type === GameUIMessageType.BUY_BONUS_SEND_BUY_BONUS_ATLAS_MAP) {
            const data: BuyBonusAtlasMapMessage = event.data

            addAtlasMap(data.atlasMap)

            if (data.atlasTexturesData) {
                handleAtlasTexturesData(data.atlasTexturesData)
            } else {
                preloadImages(Object.keys(data.atlasMap))
            }
        }
    }

    const ref = viewport === ViewportType.MOBILE_PORTRAIT ? featureOptionPortraitRef : featureOptionRef
    const scale = useScaleToFit(ref, featureAmountRef, [activeBet, viewport, isOpen])

    const isBetAllowed = (bet: number) => {
        return balance >= bet && bet <= maxBet
    }

    useEffect(() => {
        window.addEventListener('message', handleMessage, false)

        return () => {
            window.removeEventListener('message', handleMessage, false)
        }
    }, [])

    useEffect(() => {
        setActiveBetIndex(activeIndex)
    }, [activeIndex])

    useEffect(() => {
        if (betOptions.length <= 0) return

        const feature = features[selectedFeatureIndex]
        const multiplier = isGuaranteedWinEnabled
            ? (feature.guaranteedMultiplier ?? feature.multiplier)
            : feature.multiplier

        const bet = getBetValue(betOptions[activeBetIndex]) * multiplier

        setConfirmButtonDisabled(!isBetAllowed(bet))

        if (activeBetIndex <= 0) {
            setIsLeftArrowDisabled(true)
            setLeftArrowState(ArrowButtonState.DISABLED)
        } else {
            setIsLeftArrowDisabled(false)
            setLeftArrowState(ArrowButtonState.ENABLED)
        }

        if (activeBetIndex >= betOptions.length - 1) {
            setIsRightArrowDisabled(true)
            setRightArrowState(ArrowButtonState.DISABLED)

            setIsLeftArrowDisabled(false)
            setLeftArrowState(ArrowButtonState.ENABLED)
        } else {
            setIsRightArrowDisabled(false)
            setRightArrowState(ArrowButtonState.ENABLED)
        }
    }, [balance, betOptions, activeBetIndex, selectedFeatureIndex, isGuaranteed])

    useEffect(() => {
        if (!isOpen || viewport !== ViewportType.DESKTOP || !scrollRef.current) return

        const scrollContainer = scrollRef.current

        const handleWheel = (e: Event) => {
            const wheelEvent = e as WheelEvent

            if (wheelEvent.deltaY !== 0) {
                e.preventDefault()
                scrollContainer.scrollLeft += wheelEvent.deltaY
            }
        }

        scrollContainer.addEventListener('wheel', handleWheel, { passive: false })

        return () => {
            scrollContainer.removeEventListener('wheel', handleWheel)
        }
    }, [isOpen, viewport])

    const handleConfirmButtonClicked = () => {
        const selectedFeatureName = isGuaranteedWinEnabled
            ? (features[selectedFeatureIndex].guaranteedName ?? features[selectedFeatureIndex].name)
            : features[selectedFeatureIndex].name

        const setScrollingIndexMessage: EngineBetSliderSetScrollingIndexMessage = {
            type: GameUIMessageType.BET_SLIDER_SET_SCROLLING_INDEX, // TODO: rename engine message to separate from game ui message
            currentBetIndex: activeBetIndex,
            shouldPositionList: true,
        }

        window.postMessage(setScrollingIndexMessage)
        updateScrollToIndex(activeBetIndex)

        if (buyBonusConfig.shouldTheFeatureStayActive) {
            const multiplier = isGuaranteedWinEnabled
                ? (features[selectedFeatureIndex].guaranteedMultiplier ?? features[selectedFeatureIndex].multiplier)
                : features[selectedFeatureIndex].multiplier

            setMultiChoiceToggle(true)

            updateCurrentFeatureMultiplier(multiplier)
        }

        const confirmButtonClickedMessage: BuyBonusConfirmButtonClickedMessage = {
            type: GameUIMessageType.BUY_BONUS_CONFIRM_BUTTON_CLICKED,
            selectedFeatureIndex,
            betIndex: activeBetIndex,
            selectedFeatureName,
            scope: GameUIMessageScope.BUY_BONUS,
        }

        window.postMessage(confirmButtonClickedMessage)

        setActiveBetIndex(activeIndex)

        onClose()
    }

    const handleGuaranteedButtonClicked = () => {
        setIsGuaranteedWinEnabled(!isGuaranteedWinEnabled)

        const guaranteeButtonClickedMessage: BuyBonusGuaranteeButtonClickedMessage = {
            type: GameUIMessageType.BUY_BONUS_GUARANTEE_BUTTON_CLICKED,
        }

        window.postMessage(guaranteeButtonClickedMessage)
    }

    const handleBuyBonusRejected = () => {
        const buyBonusRejectedMessage: BuyBonusRejectedMessage = { type: BuyBonusMessages.BUY_BONUS_REJECTED }

        window.postMessage(buyBonusRejectedMessage)

        setActiveBetIndex(activeIndex)

        onClose()
    }

    const handleRightArrowPointerUp = () => {
        if (isRightArrowDisabled) return

        setRightArrowState(ArrowButtonState.ENABLED)

        setLeftArrowState(ArrowButtonState.ENABLED)
        setIsLeftArrowDisabled(false)
        setActiveBetIndex((prevIndex) => prevIndex + 1)

        const increaseButtonClickedMessage: BuyBonusIncreaseButtonClickedMessage = {
            type: GameUIMessageType.BUY_BONUS_INCREASE_BUTTON_CLICKED,
        }

        window.postMessage(increaseButtonClickedMessage)
    }

    const handleRightArrowPointerDown = () => {
        if (isRightArrowDisabled) return

        setRightArrowState(ArrowButtonState.PRESSED)
    }

    const handleRightArrowPointerEnter = () => {
        if (isRightArrowDisabled) return

        setRightArrowState(ArrowButtonState.HOVER)
    }

    const handleRightArrowPointerOut = () => {
        if (viewport !== ViewportType.DESKTOP) return

        setRightArrowState(isRightArrowDisabled ? ArrowButtonState.DISABLED : ArrowButtonState.ENABLED)
    }

    const handleLeftArrowPointerUp = () => {
        if (isLeftArrowDisabled) return

        setLeftArrowState(ArrowButtonState.ENABLED)
        setRightArrowState(ArrowButtonState.ENABLED)
        setIsRightArrowDisabled(false)

        setActiveBetIndex((prevIndex) => prevIndex - 1)

        const decreaseButtonClickedMessage: BuyBonusDecreaseButtonClickedMessage = {
            type: GameUIMessageType.BUY_BONUS_DECREASE_BUTTON_CLICKED,
        }

        window.postMessage(decreaseButtonClickedMessage)
    }

    const handleLeftArrowPointerDown = () => {
        if (isLeftArrowDisabled) return

        setLeftArrowState(ArrowButtonState.PRESSED)
    }

    const handleLeftArrowPointerEnter = () => {
        if (isLeftArrowDisabled) return

        setLeftArrowState(ArrowButtonState.HOVER)
    }

    const handleLeftArrowPointerOut = () => {
        if (viewport !== ViewportType.DESKTOP) return

        setLeftArrowState(isLeftArrowDisabled ? ArrowButtonState.DISABLED : ArrowButtonState.ENABLED)
    }

    const handleSelectedFeatureIndexClick = (index: number) => {
        setSelectedFeatureIndex(index)

        setSelectedFeature(features[index])

        setIsForceGuaranteedWinEnabled(features[index].guaranteedName === undefined)

        const optionButtonClickedMessage: BuyBonusOptionButtonClickedMessage = {
            type: GameUIMessageType.BUY_BONUS_OPTION_BUTTON_CLICKED,
            featureId: features[index].name,
        }

        window.postMessage(optionButtonClickedMessage)
    }

    const renderFeatureOption = (feature: BuyBonusFeature, index: number) => {
        const isSelected = index === selectedFeatureIndex
        const symbolBackgroundStyles = buildGradientStyle(
            isSelected ? layout.activeSymbolBackgroundColors : layout.symbolBackgroundColors,
            'to bottom'
        )
        const imageData = findElementByImageName(`buy-bonus-image-card-0${index + 1}.png`)

        const multiplier = isGuaranteedWinEnabled
            ? (feature.guaranteedMultiplier ?? feature.multiplier)
            : feature.multiplier
        const amount = getBetValue(activeBet) * multiplier
        const isDisabled = amount > balance || amount > maxBet

        return (
            <div
                id={`bonus-choice-${index}`}
                ref={featureOptionRef}
                key={index}
                style={{
                    ...buildGradientBorderStyle(
                        isSelected ? selectedSymbolBorderColors : symbolBorderColors,
                        isSelected ? 5 : 2,
                        10,
                        'to bottom'
                    ),
                    width: symbolOptionWidth,
                    height: symbolOptionHeight,
                    pointerEvents: isDisabled ? 'none' : 'auto',
                }}
                onClick={() => handleSelectedFeatureIndexClick(index)}
                className={clsx(
                    'cursor-pointer box-border rounded-[10px] w-full touch-manipulation',
                    portraitViewport && 'mb-5 last:mb-0'
                )}
            >
                {isDisabled && (
                    <div
                        style={{
                            position: 'absolute',
                            width: '100%',
                            top: '0px',
                            left: '0px',
                            height: '100%',
                            backgroundColor: '#7F7F7F',
                            opacity: 0.4,
                            borderRadius: '10px',
                            touchAction: 'manipulation',
                        }}
                    ></div>
                )}
                <div
                    style={{
                        ...symbolBackgroundStyles,
                        flexDirection: portraitViewport ? 'row' : 'column',
                    }}
                    className={clsx(
                        'h-full bg-gradient-to-b rounded-[8px] flex overflow-hidden items-center touch-manipulation'
                    )}
                >
                    <div
                        className={clsx(
                            'mb-[6px] flex items-center justify-center w-[240px] touch-manipulation',
                            portraitViewport ? 'mr-[20px] h-full' : 'h-[180px]'
                        )}
                    >
                        <div
                            style={{
                                ...(imageData
                                    ? buildAtlasImageStyles(
                                          imageData.atlasName,
                                          imageData.value,
                                          cachedImages[imageData.atlasName]
                                      )
                                    : {}),
                                backgroundSize: 'auto',
                                touchAction: 'manipulation',
                            }}
                            className="w-full h-full object-cover rounded-t-[8px]"
                        />
                    </div>
                    <div
                        ref={featureOptionPortraitRef}
                        className={clsx(
                            'leading-[100%] font-medium text-center touch-manipulation flex flex-col justify-around',
                            portraitViewport
                                ? 'h-full w-[380px] flex flex-col items-start justify-between text-[36px]'
                                : 'h-[96px] text-[24px]'
                        )}
                    >
                        <div
                            style={{
                                width: portraitViewport ? '360px' : '225px',
                                fontSize: feature.labelFontSize ?? (portraitViewport ? '36px' : '24px'),
                                color: isSelected
                                    ? (layout.selectedSymbolLabelColor ?? layout.symbolLabelColor)
                                    : layout.symbolLabelColor,
                            }}
                            className={clsx(
                                'font-semibold touch-manipulation text-left',
                                !portraitViewport && 'h-[36px] flex items-center justify-center text-center'
                            )}
                        >
                            {feature.label}
                        </div>
                        <div
                            ref={featureAmountRef}
                            style={{
                                zoom: scale,
                                color: isSelected
                                    ? (layout.selectedSymbolPriceColor ?? layout.symbolPriceColor)
                                    : layout.symbolPriceColor,
                            }}
                            className="font-medium touch-manipulation overflow-hidden whitespace-nowrap uppercase"
                        >
                            <span
                                className={clsx(
                                    'leading-none font-black touch-manipulation',
                                    portraitViewport ? 'text-[60px]' : 'text-[40px]'
                                )}
                            >
                                {formatMoney(amount, currencyHideCents)}
                            </span>
                            {currency}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const setArrowButtonBackgroundByState = (state: ArrowButtonState, type: ArrowButtonType) => {
        const buttonSourceData =
            findElementByImageName(`button-${type}-${state}.svg`) ||
            findElementByImageName(`button-${type}-${state}.png`)

        if (!buttonSourceData) {
            return
        }

        return buildAtlasImageStyles(
            buttonSourceData.atlasName,
            buttonSourceData.value,
            cachedImages[buttonSourceData.atlasName]
        )
    }

    const renderGuaranteedWinSection = () => {
        const isGuaranteedWinActive = features[selectedFeatureIndex]?.guaranteedName !== undefined
        const activeGuaranteedWinLabelColor = layout.activeGuaranteedWinLabelColor ?? '#FFE365'
        const guaranteedWinLabelColor = layout.guaranteedWinLabelColor ?? '#FFC865'

        return (
            <div
                style={{
                    ...(isGuaranteed ? guaranteedWinActiveBgStyle : guaranteedWinBgStyle),
                    pointerEvents: isGuaranteedWinActive ? 'auto' : 'none',
                    opacity: isGuaranteedWinActive ? 1 : 0.5,
                }}
                className={guaranteedWinBaseClasses}
            >
                <div className="inline-flex items-center">
                    <span
                        className={clsx('mr-6 text-[38px] leading-[48px] font-bold')}
                        style={{ color: isGuaranteed ? activeGuaranteedWinLabelColor : guaranteedWinLabelColor }}
                    >
                        {t('gameui.guaranteedWin')}
                    </span>
                    <div style={guaranteedWinBorderStyles} className="box-border rounded-[50px]">
                        <div
                            style={toggleBgStyle}
                            className="relative w-[132px] h-[62px] bg-gradient-to-r rounded-[50px] transition-all ease-in-out duration-350 cursor-pointer"
                            onClick={handleGuaranteedButtonClicked}
                        >
                            <img src={toggleCircle} className={togglePositionClasses} alt="GUARANTEED WIN toggle" />
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const onPointerDownDesktop = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isDesktop) return

        const el = scrollRef.current

        if (!el) return

        isDownRef.current = true
        isDraggingRef.current = false
        cancelNextClickRef.current = false

        pointerIdRef.current = e.pointerId
        startXRef.current = e.clientX
        startScrollLeftRef.current = el.scrollLeft
    }

    const onPointerMoveDesktop = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isDesktop) return

        if (!isDownRef.current) return

        const el = scrollRef.current

        if (!el) return

        const dx = e.clientX - startXRef.current

        if (!isDraggingRef.current) {
            if (Math.abs(dx) < DRAG_THRESHOLD_X) return

            isDraggingRef.current = true
            cancelNextClickRef.current = true

            el.setPointerCapture(e.pointerId)
        }

        el.scrollLeft = startScrollLeftRef.current - dx
    }

    const stopDraggingDesktop = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isDesktop) return

        isDownRef.current = false

        if (isDraggingRef.current) {
            isDraggingRef.current = false

            try {
                scrollRef.current?.releasePointerCapture(e.pointerId)
            } catch {
                console.error()
            }
        }

        pointerIdRef.current = null
    }

    // If we dragged, kill the click for this interaction only.
    // This prevents accidental "click bonus box while dragging".
    const onClickCaptureDesktop = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isDesktop) return

        if (cancelNextClickRef.current) {
            e.preventDefault()
            e.stopPropagation()
            cancelNextClickRef.current = false
        }
    }

    const renderMultiChoiceSection = () => {
        return (
            <div className={'flex justify-center'}>
                <div
                    style={{
                        ...allSymbolStyles,
                        padding: portraitViewport ? '18px' : '10px',
                    }}
                    className={clsx(
                        'flex flex-col rounded-[16px] touch-manipulation',
                        portraitViewport ? 'mb-[20px] w-full' : 'mb-[14px]',
                        shouldAddScroll && portraitViewport && 'h-[666px]',
                        !portraitViewport && shouldAddScroll && 'w-[836px]'
                    )}
                >
                    <div
                        id="multi-choice-modal"
                        ref={scrollRef}
                        style={{
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                        }}
                        className={clsx(
                            'flex justify-between gap-x-[14px] touch-manipulation overflow-scroll',
                            portraitViewport ? 'flex-col' : 'mb-[0px]',
                            !portraitViewport && shouldAddScroll && 'w-[808px]'
                        )}
                        onPointerDown={isDesktop ? onPointerDownDesktop : undefined}
                        onPointerMove={isDesktop ? onPointerMoveDesktop : undefined}
                        onPointerUp={isDesktop ? stopDraggingDesktop : undefined}
                        onPointerCancel={isDesktop ? stopDraggingDesktop : undefined}
                        onPointerLeave={isDesktop ? stopDraggingDesktop : undefined}
                        onClickCapture={isDesktop ? onClickCaptureDesktop : undefined}
                    >
                        {features.map((feature, index) => renderFeatureOption(feature, index))}
                    </div>
                    {hasGuaranteedWin && renderGuaranteedWinSection()}
                </div>
            </div>
        )
    }

    // TODO: should the feature name translations come from the buy bonus configuration or from localization
    const renderSingleChoiceSection = () => {
        const imageData = findElementByImageName('buy-bonus-image-card.png')
        const { labelColor, moneyColor } = layout
        const amount = getBetValue(activeBet) * features[selectedFeatureIndex].multiplier

        return (
            <div className="pb-2.5">
                {imageData && (
                    <div className="mb-2.5">
                        <div
                            style={buildAtlasImageStyles(
                                imageData.atlasName,
                                imageData.value,
                                cachedImages[imageData.atlasName]
                            )}
                        />
                    </div>
                )}
                <div className="text-4xl leading-none text-center">
                    <div
                        id="bonus-title"
                        className="mb-1 font-semibold [text-shadow:0_2px_2px_rgba(0,0,0,0.8)]"
                        style={{ color: labelColor }}
                    >
                        {features[selectedFeatureIndex].label}
                    </div>
                    <div
                        id="bonus-amount"
                        className="text-6xl font-black flex items-baseline justify-center"
                        style={{ color: moneyColor }}
                    >
                        <div>{formatMoney(amount, currencyHideCents)}</div>
                        <div className="font-medium ml-[5px] text-[36px]">{currency}</div>
                    </div>
                </div>
            </div>
        )
    }

    const headerGradientStyle = buildGradientStyle(layout.headerColors)
    const bodyGradientStyle = buildGradientStyle(layout.bodyColors, 'to bottom')
    const footerGradientStyle = buildGradientStyle(layout.footerColors)
    const betAmountBorderStyle = buildGradientStyle(layout.amountInputBorderColors, 'to bottom')

    return (
        <BonusModal isOpen={isOpen} onClose={handleBuyBonusRejected} showCloseIcon={true} width={layout.modalWidth}>
            <BonusModalHeader gradientStyle={headerGradientStyle}>
                <span id="bonus-name" className="text-[40px] font-bold" style={{ color: layout.headerLabelColor }}>
                    {t('gameui.buyBonus')}
                </span>
            </BonusModalHeader>

            <BonusModalBody gradientStyle={bodyGradientStyle}>
                <div style={{ padding: modalBodyPadding }}>
                    {isMultiChoice ? renderMultiChoiceSection() : renderSingleChoiceSection()}
                    <div className={clsx('flex items-center justify-center')}>
                        <button
                            id="bonus-arrow-left"
                            disabled={isLeftArrowDisabled}
                            onPointerDown={handleLeftArrowPointerDown}
                            onPointerUp={handleLeftArrowPointerUp}
                            onPointerEnter={handleLeftArrowPointerEnter}
                            onPointerOut={handleLeftArrowPointerOut}
                            style={{
                                ...setArrowButtonBackgroundByState(leftArrowState, ArrowButtonType.DECREASE),
                                marginRight: '10px',
                            }}
                        ></button>
                        <div
                            className="p-[4px] rounded-[18px] bg-gradient-to-b from-[#AB540C] via-[#FFF2B4] to-[#AB540C] inline-block"
                            style={{ ...betAmountBorderStyle }}
                        >
                            <div
                                id="bonus-active-bet-panel"
                                style={{ ...betAmountBgStyle }}
                                className="w-[300px] h-[115px] flex flex-col items-center justify-center rounded-[18px]"
                            >
                                <span
                                    id="bonus-active-bet-currency"
                                    className="mb-[10px] text-[18px] leading-[16px] font-bold text-[#FFD100]"
                                >
                                    {currency}
                                </span>
                                <span id="bonus-active-bet-amount" className="mb-2 text-[46px] leading-none font-black">
                                    {formatMoney(getBetValue(activeBet), currencyHideCents)}
                                </span>
                                <span
                                    id="bonus-active-bet-title"
                                    className="text-[24px] leading-[24px] font-bold text-[#FFD100]"
                                >
                                    {t('gameui.bet')}
                                </span>
                            </div>
                        </div>
                        <button
                            id="bonus-arrow-right"
                            disabled={isRightArrowDisabled}
                            onPointerDown={handleRightArrowPointerDown}
                            onPointerUp={handleRightArrowPointerUp}
                            onPointerEnter={handleRightArrowPointerEnter}
                            onPointerOut={handleRightArrowPointerOut}
                            style={{
                                ...setArrowButtonBackgroundByState(rightArrowState, ArrowButtonType.INCREASE),
                                marginLeft: '10px',
                            }}
                        ></button>
                    </div>
                </div>
            </BonusModalBody>

            <BonusModalFooter gradientStyle={footerGradientStyle}>
                <button
                    id="bonus-buy-button"
                    className="mt-[10px]"
                    disabled={confirmButtonDisabled}
                    onClick={handleConfirmButtonClicked}
                >
                    <div className={`relative  ${confirmButtonDisabled && 'opacity-50'}`}>
                        <img src={buyButton} alt="Buy Bonus button" className="mx-auto" />
                        <div
                            className="absolute top-[50%] left-[50%] text-white font-bold text-3xl w-full"
                            style={{
                                transform: 'translate(-50%, -50%)',
                            }}
                        >
                            {t('gameui.buy')}
                        </div>
                    </div>
                </button>
                <div
                    id="bonus-balance-panel"
                    className="text-[24px] leading-none font-bold mb-[8px] flex items-baseline justify-center uppercase"
                    style={{ color: layout.balanceColor }}
                >
                    {`${t('common.balance')}: `}
                    <span className="text-[40px] leading-1 font-black mx-[8px]">
                        {formatMoney(balance, currencyHideCents)}
                    </span>
                    {currency}
                </div>
            </BonusModalFooter>
        </BonusModal>
    )
}

export default BuyBonusModal
